import { PhoneArrowUpRightIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  useGetEnhancedBlandCallWorkflowsQuery,
  useRefreshEnhancedBlandCallWorkflowStatusMutation,
  useRetryEnhancedBlandCallWorkflowMutation,
  useStartEnhancedBlandCallWorkflowMutation,
} from '@/services/api/blandService';
import { Task } from '@/services/types/client-intake-types';
import { CanonicalTaskType } from '@/services/types/task-types';
import {
  EnhancedBlandWorkflowState,
  EnhancedBlandWorkflowStatus,
} from '@/types/enhanced-bland-call';

import { VoiceAgentSummaryCard } from './cards/VoiceAgentSummaryCard';
import { VoiceAgentContactMethodCard } from './components/VoiceAgentContactMethodCard';
import { VoiceAgentInsuranceContactCard } from './components/VoiceAgentInsuranceContactCard';
import { VoiceAgentRunModal } from './modals/VoiceAgentRunModal';

interface VoiceAgentButtonProps {
  task: Task;
  matterId: string;
  className?: string;
}

export const VoiceAgentButton: React.FC<VoiceAgentButtonProps> = ({
  task,
  matterId,
  className = '',
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [lastKnownStatus, setLastKnownStatus] =
    useState<EnhancedBlandWorkflowStatus | null>(null);
  const [preferredPhoneNumber, setPreferredPhoneNumber] = useState<
    string | null
  >(null);
  const [useHoldAndTransfer, setUseHoldAndTransfer] = useState(false);

  // Check if this is a claim task (eligible for Hold & Transfer)
  const isClaimTask = task.type?.canonical_type === CanonicalTaskType.OPEN_CLAIM;

  const {
    data: workflowsData,
    isLoading: workflowsLoading,
    error: workflowsError,
    refetch: refetchWorkflows,
  } = useGetEnhancedBlandCallWorkflowsQuery(
    { matterId },
    {
      pollingInterval: 60000,
      skipPollingIfUnfocused: false,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const [startWorkflow, { isLoading: startLoading }] =
    useStartEnhancedBlandCallWorkflowMutation();

  const [retryWorkflow, { isLoading: retryLoading }] =
    useRetryEnhancedBlandCallWorkflowMutation();

  const [refreshStatus, { isLoading: refreshLoading }] =
    useRefreshEnhancedBlandCallWorkflowStatusMutation();

  const taskWorkflow: EnhancedBlandWorkflowState | undefined =
    workflowsData?.workflows.find((w) => w.task_id === task.id);

  // toast on status change
  useEffect(() => {
    if (taskWorkflow?.status && taskWorkflow.status !== lastKnownStatus) {
      if (lastKnownStatus !== null) {
        const statusMessages = {
          [EnhancedBlandWorkflowStatus.SCHEDULED]: 'Voice agent scheduled',
          [EnhancedBlandWorkflowStatus.STARTED]: 'Voice agent starting...',
          [EnhancedBlandWorkflowStatus.FAILED_READY_CHECK]:
            'Missing required information',
          [EnhancedBlandWorkflowStatus.CALL_IN_PROGRESS]:
            '📞 AI is making the call!',
          [EnhancedBlandWorkflowStatus.THINKING]: 'Processing results...',
          [EnhancedBlandWorkflowStatus.WAITING]: 'Agent waiting...',
          [EnhancedBlandWorkflowStatus.COMPLETED]:
            '✅ Call completed successfully!',
          [EnhancedBlandWorkflowStatus.FAILED]: '❌ Workflow failed',
          [EnhancedBlandWorkflowStatus.FAILED_INELIGIBLE]:
            'Task not eligible for AI calling',
          [EnhancedBlandWorkflowStatus.CANCELLED]: 'Task cancelled',
        } as const;

        const message = statusMessages[taskWorkflow.status];
        if (message) {
          if (taskWorkflow.status === EnhancedBlandWorkflowStatus.COMPLETED) {
            toast.success(message);
          } else if (
            [
              EnhancedBlandWorkflowStatus.FAILED,
              EnhancedBlandWorkflowStatus.FAILED_INELIGIBLE,
            ].includes(taskWorkflow.status)
          ) {
            toast.error(message);
          } else {
            toast.info(message);
          }
        }
      }
      setLastKnownStatus(taskWorkflow.status);
    }
  }, [taskWorkflow?.status, lastKnownStatus]);

  const handleStartWorkflow = useCallback(async () => {
    try {
      await startWorkflow({
        matterId,
        taskId: task.id,
        ...(preferredPhoneNumber ? { preferredPhoneNumber } : {}),
        ...(isClaimTask && useHoldAndTransfer
          ? { useHoldAndTransfer: true }
          : {}),
      }).unwrap();
      toast.success(
        useHoldAndTransfer && isClaimTask
          ? 'AI calling and will transfer when someone answers!'
          : 'Voice agent started!',
      );
      refetchWorkflows();
    } catch (error: any) {
      const errorMessage = error?.data?.error || 'Failed to start voice agent';
      if (
        errorMessage.includes('already exists') ||
        errorMessage.includes('already running')
      ) {
        toast.warning('Voice agent is already running for this task');
        refetchWorkflows();
        return;
      }
      toast.error(errorMessage);
    }
  }, [
    matterId,
    task.id,
    preferredPhoneNumber,
    useHoldAndTransfer,
    isClaimTask,
    startWorkflow,
    refetchWorkflows,
  ]);

  const handleRetryWorkflow = useCallback(async () => {
    try {
      await retryWorkflow({ matterId, taskId: task.id }).unwrap();
      toast.success('Status updated!');
      refetchWorkflows();
    } catch (error: any) {
      toast.error(error?.data?.error || 'Failed to update workflow');
    }
  }, [matterId, task.id, retryWorkflow, refetchWorkflows]);

  const handleRefreshStatus = useCallback(async () => {
    try {
      await refreshStatus({ matterId, taskId: task.id }).unwrap();
      toast.success('Status refreshed!');
      refetchWorkflows();
    } catch (error: any) {
      toast.error(error?.data?.error || 'Failed to refresh status');
    }
  }, [matterId, task.id, refreshStatus, refetchWorkflows]);

  const handleViewDetails = useCallback(() => setModalOpen(true), []);

  if (workflowsError && !workflowsData) {
    return (
      <div className={`text-red-600 text-sm ${className}`}>
        Failed to load workflow status
      </div>
    );
  }

  return (
    <div className={`${className} space-y-3`}>
      <VoiceAgentSummaryCard
        task={task}
        workflow={taskWorkflow || null}
        isLoading={workflowsLoading}
        onStart={handleStartWorkflow}
        onRetry={handleRetryWorkflow}
        onRefresh={handleRefreshStatus}
        onViewDetails={handleViewDetails}
        retryLoading={retryLoading}
        refreshLoading={refreshLoading}
      />
      {/* Phone selector for treatment/medical records tasks */}
      {task.treatment_record_request?.id && (
        <VoiceAgentContactMethodCard
          task={task}
          matterId={matterId}
          onContactNumberChange={setPreferredPhoneNumber}
        />
      )}

      {/* Phone selector for claims/insurance tasks */}
      {isClaimTask && (
        <VoiceAgentInsuranceContactCard
          task={task}
          matterId={matterId}
          onContactNumberChange={setPreferredPhoneNumber}
        />
      )}

      {/* AI Hold & Transfer toggle for claim tasks */}
      {isClaimTask && (
        // eslint-disable-next-line jsx-a11y/label-has-associated-control -- label wraps checkbox input
        <label className="block bg-white border border-gray-200 rounded-md p-3 cursor-pointer hover:bg-gray-50 transition-colors">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={useHoldAndTransfer}
              onChange={(e) => setUseHoldAndTransfer(e.target.checked)}
              disabled={startLoading}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <PhoneArrowUpRightIcon className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                  AI Hold & Transfer
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                AI navigates the phone system and waits on hold. When a human
                answers, you&apos;ll get a Slack notification and the call
                transfers to your phone.
              </p>
            </div>
          </div>
        </label>
      )}

      <VoiceAgentRunModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        task={task}
        matterId={matterId}
        workflow={taskWorkflow || null}
        onRefetch={refetchWorkflows}
        onStartWorkflow={handleStartWorkflow}
        isStartingWorkflow={startLoading}
      />
    </div>
  );
};
